<template>
  <div class="add-year-modal" :class="`add-year-modal--${target}`">
    <div
      class="add-year-modal__background"
      @click.stop="$emit('close-modal')"
    ></div>
    <div class="add-year-modal__wrapper">
      <div class="add-year-modal__header">
        <div class="add-year-modal__title">
          {{ titleText }}
        </div>
        <span class="add-year-modal__close-icon">
          <button @click="$emit('close-modal')" class="add-year-modal__close">
            <span class="add-year-modal__close--icon material-icons">
              clear
            </span>
          </button>
        </span>
      </div>
      <div class="add-year-modal__body">
        <div class="add-year-modal__left" v-if="!isForPremium">
          <div class="add-year-modal__left-wrapper">
            <div class="add-year-modal__pill">
              You're saving 22%
            </div>
            <h3 class="add-year-modal__plan-title">Premium</h3>
            <div class="add-year-modal__price">
              <span class="add-year-modal__price--old">$749</span>
              <span class="add-year-modal__price--new">$584.22</span>
              <span class="add-year-modal__price--period">First Year*</span>
            </div>
            <div class="add-year-modal__line"></div>
            <div class="add-year-modal__feature-list">
              <div
                class="add-year-modal__feature-list__item"
                v-for="feature in featureList"
                :key="feature"
              >
                <GreenCheck /> {{ feature }}
              </div>
            </div>
          </div>
          <div class="add-year-modal__aclaration">
            *After the first year the renewal cost will be $749 / year.
          </div>
        </div>
        <div class="add-year-modal__right">
          <div class="add-year-modal__right-title">Change summary</div>
          <div class="add-year-modal__right-text" v-if="isForPremium">
            By participating in this offer, you're adding another year of INE
            Premium access to your existing subscription at a 22% discount. Your
            credit card will be charged $584.22 USD today and your subscription
            will automatically renew at your current subscription price for
            Premium.<br />
            Questions? Contact us at
            <a href="mailto:support@ine.com">support@ine.com</a>.
          </div>
          <div class="add-year-modal__right-text" v-else>
            If you participate in this limited time offer, you'll be upgraded to
            our Premium plan for only $584.22 USD (normally priced at $749/year
            USD). You'll be charged $584.22 USD today and your plan will
            auto-renew at $749 USD. You'll be charged $749 USD every year while
            your subscription remains active. You may cancel your subscription
            at any time.
            <br />
            Questions? Contact us at
            <a href="mailto:support@ine.com">support@ine.com</a>.
          </div>
          <div class="add-year-modal__right-change">
            We’ll charge your
            {{
              isCreditCard ? `card ending in ${last4OfCC}` : 'Paypal account'
            }}.
            <a @click="updateBilling">Update billing information</a>
          </div>
          <div class="add-year-modal__expiration">
            <div class="add-year-modal__expiration-label">
              New Renewal date:
            </div>
            <div class="add-year-modal__expiration-date">
              {{ expirationDateFirstPart }},&nbsp;
              <span class="add-year-modal__expiration-date--crossed">
                {{ expirationDateYear }} </span
              >&nbsp;{{ expirationDateYear + 1 }}
            </div>
          </div>
        </div>
        <div class="add-year-modal__action-buttons">
          <a @click="$emit('close-modal')" class="btn btn--secondary">Cancel</a>
          <a
            @click="confirmUpgrading"
            class="btn btn--primary"
            :disabled="upgrading"
            >{{ buttonText }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { format } from 'date-fns'
import GreenCheck from '@/assets/icons/greencheck.svg'

export default {
  name: 'AddYearModal',
  components: {
    GreenCheck: { ...GreenCheck, name: 'GreenCheck' },
  },
  props: {
    promotion: {
      type: Object,
      required: true,
    },
    target: {
      type: String,
      required: true,
    },
  },
  data: () => {
    return {
      featureList: [
        'Access entire course library',
        'Github and Azure projects',
        'Hands-on Labs',
        'Live Online Bootcamps',
        'Networking Workbooks',
      ],
      upgrading: false,
    }
  },
  computed: {
    ...mapState({
      paymentInfo: state => state.subscriptions.paymentInfo,
      promotionsAvailable: state => state.subscriptions.promotionsAvailable,
    }),
    ...mapGetters({
      canViewBillingInfo: 'subscriptions/canViewBillingInfo',
      expirationDateFirstPart: 'subscriptions/expirationDateFirstPart',
      expirationDateYear: 'subscriptions/expirationDateYear',
    }),
    last4OfCC() {
      const {
        paymentInfo: { last4_of_cc },
      } = this
      return last4_of_cc || ''
    },
    isCreditCard() {
      const {
        paymentInfo: { payment_method },
      } = this
      return payment_method === 'credit_card'
    },
    content() {
      return this.promotion ? this.promotion.content : {}
    },
    expirationDateFirstPart() {
      if (!this.content.renews_at) return
      return format(new Date(this.content.renews_at), 'LLL d')
    },
    expirationDateYear() {
      if (!this.content.renews_at) return
      return parseInt(format(new Date(this.content.renews_at), 'yyyy'))
    },
    isForPremium() {
      return this.target === 'premium'
    },
    titleText() {
      return this.isForPremium
        ? 'Ring in 2022 with 22% in savings on Premium!'
        : 'Ring in 2022 with 22% in savings on Premium!'
    },
    buttonText() {
      return this.isForPremium ? 'Confirm Purchase' : 'Confirm upgrade'
    },
  },
  methods: {
    updateBilling() {
      this.$emit('update-billing')
      this.$emit('close-modal')
    },
    async confirmUpgrading() {
      if (this.upgrading) return
      this.upgrading = true
      try {
        await this.$store.dispatch('subscriptions/submitPromotion', {
          promotion_slug: this.promotion.promo_slug,
          ...this.promotion.action.promo_fields,
        })
        this.$emit('close-modal')
        this.$store.dispatch(
          'app/setNotification',
          'Your upgrade is being processed and should be complete momentarily. Please check back later. A new invoice will be sent to your email'
        )
      } catch (error) {
        this.$emit('close-modal')
      }
    },
  },
  watch: {
    canViewBillingInfo: {
      immediate: true,
      async handler() {
        if (!this.paymentInfo.payment_method) {
          await this.$store.dispatch('subscriptions/getPaymentInfo')
        }
      },
    },
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('dialog-modal-open')
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('dialog-modal-open')
  },
}
</script>
<style lang="scss" scoped>
.add-year-modal {
  $this: &;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  overflow: auto;
  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    margin: 0px auto;
    background-color: var(--darker-gray);
    color: var(--white);
    max-width: 856px;
    height: 100%;
    font-size: 16px;
    overflow: auto;
    @include mobile {
      border-radius: 35px;
      height: auto;
    }
  }
  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: saturate(180%) blur(20px);
  }
  &__close {
    width: 50px;
    height: 50px;
    right: 20px;
    top: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--white);
    @include mobile {
      position: absolute;
    }
    &--icon {
      fill: var(--white);
    }
    &:hover {
      opacity: 0.75;
    }
  }
  &__header {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background: linear-gradient(89.99deg, #863ad2 3.58%, #5936e4 97.14%);
    border-radius: 35px 35px 0px 0px;
    width: 100%;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
  }
  &__body {
    padding: 60px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  &__left {
    width: 100%;
    margin-bottom: 30px;
    @include mobile {
      width: 50%;
      margin-bottom: 0;
    }
    &-wrapper {
      background: linear-gradient(
        104.99deg,
        rgba(62, 63, 73, 0.7) -13.91%,
        rgba(62, 63, 73, 0) 100.48%
      );
      border: 1px solid #a793f6;
      padding: 30px 35px;
      border-radius: 10px;
    }
  }
  &__pill {
    width: 155px;
    padding: 9px 14px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    font-size: 12px;
    line-height: 14px;
    display: flex;
    align-items: center;
    text-transform: uppercase;
    color: #a793f6;
  }
  &__plan-title {
    margin: 30px 0 0 0;
    font-size: 32px;
    line-height: 38px;
  }
  &__right {
    width: 100%;
    a {
      cursor: pointer;
      color: #d3c9fb;
      &:hover {
        opacity: 0.75;
      }
    }
    @include mobile {
      width: 50%;
      padding: 0 0 0 30px;
    }
    &-title {
      font-size: 20px;
      line-height: 24px;
      margin-bottom: 30px;
    }
    &-text {
      font-size: 16px;
      line-height: 26px;
      color: var(--light-blue-gray);
      margin-bottom: 30px;
    }
    &-change {
      font-size: 16px;
      line-height: 26px;
      color: #ffffff;
      margin-bottom: 30px;
    }
  }
  &__feature-list {
    &__item {
      margin-bottom: 5px;
      svg {
        width: 16px;
      }
    }
  }
  &__price {
    font-weight: bold;
    font-family: var(--font-bold);
    display: flex;
    align-content: center;
    align-items: center;
    margin-bottom: 30px;
    &--new {
      font-size: 54px;
      line-height: 52px;
      padding: 0px 5px;
    }
    &--old {
      font-size: 40px;
      line-height: 52px;
      color: #ffffff;
      opacity: 0.8;
      position: relative;
      &:before {
        content: '';
        position: absolute;
        transform: rotate(-165deg);
        width: 90px;
        height: 2px;
        top: 25px;
        background: #ff5e5e;
        left: -1px;
      }
      &:after {
        content: '';
        position: absolute;
        transform: rotate(164deg);
        width: 90px;
        height: 2px;
        top: 28px;
        background: #ff5e5e;
        left: -1px;
      }
    }
    &--period {
      font-family: var(--font-regular);
      font-weight: normal;
      font-size: 12px;
      line-height: 14px;
      display: flex;
      align-items: center;
      text-transform: uppercase;
      color: var(--light-blue-gray);
      width: 50px;
    }
  }
  &__line {
    margin-bottom: 30px;
    border: 1px solid rgba(62, 63, 73, 0.4);
  }
  &__aclaration {
    font-size: 12px;
    line-height: 14px;
    color: var(--light-blue-gray);
    margin-top: 10px;
  }
  &__action-buttons {
    margin-top: 35px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    @include mobile {
      width: auto;
      flex-wrap: nowrap;
    }
    a {
      width: 100%;
      margin-bottom: 20px;
      @include mobile {
        width: auto;
        margin-bottom: 0;
        margin-left: 20px;
      }
      &:disabled,
      &[disabled] {
        pointer-events: none;
      }
    }
  }
  &__expiration {
    &-label {
      font-size: 12px;
      line-height: 14px;
      display: flex;
      align-items: center;
      text-transform: uppercase;
      opacity: 0.7;
      margin-bottom: 5px;
    }
    &-date {
      font-size: 18px;
      line-height: 24px;
      display: flex;
      align-items: center;
      &--crossed {
        position: relative;
        &:before {
          content: '';
          position: absolute;
          transform: rotate(-165deg);
          width: 43px;
          height: 2px;
          top: 11px;
          background: #ff5e5e;
          left: -1px;
        }
        &:after {
          content: '';
          position: absolute;
          transform: rotate(164deg);
          width: 43px;
          height: 2px;
          top: 12px;
          background: #ff5e5e;
          left: -1px;
        }
      }
    }
  }
  &--premium {
    #{$this}__right {
      padding: 0;
      width: 100%;
    }
  }
}
</style>
